import React, { useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Chip,
  Typography,
  IconButton,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { X } from 'lucide-react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import dayjs, { Dayjs } from 'dayjs';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';

import { ProjectStatus, ProjectType, TaskPriority, Project } from '@/types';
import { projectService, userService } from '@/services/api';
import { QUERY_KEYS } from '@/services/queryClient';
import type { User } from '@/types';

// Form validation schema
const projectSchema = z.object({
  name: z.string().optional().or(z.literal('')),
  description: z.string().optional().or(z.literal('')),
  status: z.enum(['not_started', 'planning', 'in_progress', 'on_hold', 'completed', 'cancelled'] as const),
  priority: z.enum(['low', 'medium', 'high', 'critical'] as const),
  projectType: z.nativeEnum(ProjectType).optional(),
  startDate: z.any().nullable().optional(),
  endDate: z.any().nullable().optional(),
  parentProjectId: z.string().optional(),
  teamMembers: z.array(z.string()).optional(),
});

type ProjectFormData = z.infer<typeof projectSchema>;

interface CreateProjectModalProps {
  open: boolean;
  onClose: () => void;
  parentProject?: {
    id: string;
    name: string;
  };
  initialData?: Record<string, unknown>;
}

function userDisplayName(u: User): string {
  return u.displayName || [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email;
}

/**
 * CreateProjectModal
 * Modal for creating new projects or subprojects
 */
const CreateProjectModal: React.FC<CreateProjectModalProps> = ({
  open,
  onClose,
  parentProject,
  initialData,
}) => {
  const isEditMode = Boolean(initialData);
  const queryClient = useQueryClient();

  const { data: projectsData } = useQuery({
    queryKey: QUERY_KEYS.projects,
    queryFn: () => projectService.getProjects(undefined, 1, 200),
    enabled: open && !parentProject,
  });
  const parentProjects = projectsData?.items ?? [];

  const { data: usersData } = useQuery({
    queryKey: QUERY_KEYS.users,
    queryFn: () => userService.getUsers({ pageSize: 100 }),
    enabled: open,
  });
  const users = usersData ?? [];
  
  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<ProjectFormData>({
    resolver: zodResolver(projectSchema),
    defaultValues: {
      name: (initialData?.name as string) || '',
      description: (initialData?.description as string) || '',
      status: (initialData?.status as ProjectFormData['status']) || 'planning',
      priority: (initialData?.priority as ProjectFormData['priority']) || 'medium',
      projectType: (initialData?.projectType as ProjectType) || ProjectType.INTERNAL,
      startDate: initialData?.startDate ? dayjs(initialData.startDate as string) : null,
      endDate: initialData?.endDate ? dayjs(initialData.endDate as string) : null,
      parentProjectId: parentProject?.id || '',
      teamMembers: (initialData?.teamMembers as string[]) || [],
    },
  });

  const startDate = watch('startDate');

  // Create project mutation
  const createMutation = useMutation({
    mutationFn: (data: Partial<Project>) => projectService.createProject(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.projects });
      handleClose();
    },
  });

  // Reset form when modal opens
  useEffect(() => {
    if (open && !isEditMode) {
      reset({
        name: '',
        description: '',
        status: 'planning',
        priority: 'medium',
        projectType: ProjectType.INTERNAL,
        startDate: null,
        endDate: null,
        parentProjectId: parentProject?.id || '',
        teamMembers: [],
      });
    }
  }, [open, isEditMode, parentProject?.id, reset]);

  const onSubmit = async (data: ProjectFormData) => {
    const payload: Partial<Project> = {
      name: data.name || 'Untitled Project',
      description: data.description || '',
      status: data.status === 'planning' ? ProjectStatus.PLANNING 
        : data.status === 'in_progress' ? ProjectStatus.ACTIVE
        : data.status === 'on_hold' ? ProjectStatus.ON_HOLD
        : data.status === 'completed' ? ProjectStatus.COMPLETED
        : data.status === 'cancelled' ? ProjectStatus.CANCELLED
        : ProjectStatus.PLANNING,
      projectType: data.projectType || ProjectType.INTERNAL,
      startDate: data.startDate ? dayjs(data.startDate).toISOString() : dayjs().toISOString(),
      endDate: data.endDate ? dayjs(data.endDate).toISOString() : dayjs().add(30, 'day').toISOString(),
      priority: data.priority as any,
    } as any;
    
    // Add team members if provided
    if (data.teamMembers && data.teamMembers.length > 0) {
      (payload as any).teamMemberIds = data.teamMembers;
    }
    
    createMutation.mutate(payload);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { maxHeight: '90vh' },
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h6" fontWeight={600}>
            {parentProject ? 'Create Subproject' : 'Create New Project'}
          </Typography>
          {parentProject && (
            <Typography variant="caption" color="text.secondary">
              Parent: {parentProject.name}
            </Typography>
          )}
        </Box>
        <IconButton onClick={handleClose} size="small">
          <X size={20} />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent dividers>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Project Name */}
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Project Name"
                    placeholder="Enter project name"
                    error={!!errors.name}
                    helperText={errors.name?.message}
                    fullWidth
                  />
                )}
              />

              {/* Description */}
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Description"
                    placeholder="Describe the project objectives and scope"
                    multiline
                    rows={3}
                    error={!!errors.description}
                    helperText={errors.description?.message}
                    fullWidth
                  />
                )}
              />

              {/* Status and Priority */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Controller
                  name="status"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.status}>
                      <InputLabel>Status</InputLabel>
                      <Select {...field} label="Status">
                        <MenuItem value="planning">Planning</MenuItem>
                        <MenuItem value="not_started">Not Started</MenuItem>
                        <MenuItem value="in_progress">In Progress</MenuItem>
                        <MenuItem value="on_hold">On Hold</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                />

                <Controller
                  name="priority"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.priority}>
                      <InputLabel>Priority</InputLabel>
                      <Select {...field} label="Priority">
                        <MenuItem value="low">Low</MenuItem>
                        <MenuItem value="medium">Medium</MenuItem>
                        <MenuItem value="high">High</MenuItem>
                        <MenuItem value="critical">Critical</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                />

                <Controller
                  name="projectType"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.projectType}>
                      <InputLabel>Project Type</InputLabel>
                      <Select {...field} label="Project Type">
                        <MenuItem value={ProjectType.INTERNAL}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.main' }} />
                            Internal
                          </Box>
                        </MenuItem>
                        <MenuItem value={ProjectType.CUSTOMER}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'success.main' }} />
                            Customer
                          </Box>
                        </MenuItem>
                        <MenuItem value={ProjectType.ONSITE}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'info.main' }} />
                            Onsite
                          </Box>
                        </MenuItem>
                      </Select>
                    </FormControl>
                  )}
                />
              </Box>

              {/* Dates */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Controller
                  name="startDate"
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      {...field}
                      label="Start Date"
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!errors.startDate,
                          helperText: errors.startDate?.message as string,
                        },
                      }}
                    />
                  )}
                />

                <Controller
                  name="endDate"
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      {...field}
                      label="End Date"
                      minDate={startDate ? dayjs(startDate) : undefined}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!errors.endDate,
                          helperText: errors.endDate?.message as string,
                        },
                      }}
                    />
                  )}
                />
              </Box>

              {/* Parent Project (for subprojects) */}
              {!parentProject && (
                <Controller
                  name="parentProjectId"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>Parent Project (optional)</InputLabel>
                      <Select {...field} label="Parent Project (optional)">
                        <MenuItem value="">None (Top-level project)</MenuItem>
                        {parentProjects.map((project) => (
                          <MenuItem key={project.id} value={project.id}>
                            {project.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              )}

              {/* Team Members */}
              <Controller
                name="teamMembers"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    multiple
                    options={users}
                    getOptionLabel={(option) => userDisplayName(option)}
                    value={users.filter((u) => (field.value || []).includes(u.id))}
                    onChange={(_, newValue) => {
                      field.onChange(newValue.map((v) => v.id));
                    }}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={option.id}
                          label={userDisplayName(option)}
                          size="small"
                        />
                      ))
                    }
                    renderOption={(props, option) => (
                      <Box component="li" {...props} key={option.id}>
                        <Box>
                          <Typography variant="body2">{userDisplayName(option)}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {option.email}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Team Members"
                        placeholder="Select team members"
                        error={!!errors.teamMembers}
                        helperText={errors.teamMembers?.message}
                      />
                    )}
                  />
                )}
              />

              {/* Info Banner */}
              <Box
                sx={{
                  bgcolor: 'rgba(33, 150, 243, 0.08)',
                  borderRadius: 1,
                  p: 2,
                  border: '1px solid',
                  borderColor: 'info.main',
                }}
              >
                <Typography variant="caption" color="info.main">
                  💡 You can add tasks, milestones, and detailed RACI assignments after creating the project.
                </Typography>
              </Box>
            </Box>
          </LocalizationProvider>
        </DialogContent>

        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={handleClose} variant="outlined" color="inherit">
            Cancel
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            disabled={isSubmitting || createMutation.isPending}
          >
            {(isSubmitting || createMutation.isPending) ? 'Creating...' : 'Create Project'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CreateProjectModal;
